import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { GraduationCap, Building2, Briefcase, Users, ArrowRight, Sparkles, Clock, Wallet, Zap, Globe } from 'lucide-react';

// Composant Robot Souriant
const SmilingRobot = ({ className = "w-6 h-6" }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <rect x="4" y="4" width="16" height="16" rx="4" fill="currentColor" opacity="0.2"/>
    <rect x="4" y="4" width="16" height="16" rx="4" stroke="currentColor" strokeWidth="2"/>
    <circle cx="9" cy="10" r="1.5" fill="currentColor"/>
    <circle cx="15" cy="10" r="1.5" fill="currentColor"/>
    <path d="M8 14.5C8 14.5 9.5 17 12 17C14.5 17 16 14.5 16 14.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <rect x="10" y="1" width="4" height="3" rx="1" fill="currentColor"/>
  </svg>
);

export const Landing = () => {
  const { t } = useLanguage();

  const branches = [
    {
      id: 'formation',
      icon: GraduationCap,
      title: t('branchFormation'),
      description: t('branchFormationDesc'),
      image: 'https://images.unsplash.com/photo-1758691736067-b309ee3ef7b9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjB0cmFpbmluZyUyMHdvcmtzaG9wJTIwZGl2ZXJzZSUyMGdyb3VwJTIwbW9kZXJufGVufDB8fHx8MTc2ODI1MzgxNXww&ixlib=rb-4.1.0&q=85',
      path: '/formation',
      color: 'bg-blue-500'
    },
    {
      id: 'pme',
      icon: Building2,
      title: t('branchPME'),
      description: t('branchPMEDesc'),
      image: 'https://images.unsplash.com/photo-1565688527174-775059ac429c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxzbWFsbCUyMGJ1c2luZXNzJTIwdGVhbSUyMG1lZXRpbmclMjBtb2Rlcm4lMjBvZmZpY2UlMjBjb2xsYWJvcmF0aW9ufGVufDB8fHx8MTc2ODI1MzgxN3ww&ixlib=rb-4.1.0&q=85',
      path: '/pme',
      color: 'bg-emerald-500'
    },
    {
      id: 'freelance',
      icon: Briefcase,
      title: t('branchFreelance'),
      description: t('branchFreelanceDesc'),
      image: 'https://images.unsplash.com/photo-1696087225391-eb97abf5ba20?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxjcmVhdGl2ZSUyMGZyZWVsYW5jZXIlMjB3b3JraW5nJTIwaG9tZSUyMG9mZmljZSUyMGFlc3RoZXRpY3xlbnwwfHx8fDE3NjgyNTM4MTl8MA&ixlib=rb-4.1.0&q=85',
      path: '/freelance',
      color: 'bg-purple-500'
    },
    {
      id: 'employes',
      icon: Users,
      title: t('branchEmployes'),
      description: t('branchEmployesDesc'),
      image: 'https://images.unsplash.com/photo-1748346918817-0b1b6b2f9bab?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxmb2N1c2VkJTIwb2ZmaWNlJTIwd29ya2VyJTIwZGF0YSUyMGFuYWx5c3QlMjBtb2Rlcm4lMjBkZXNrfGVufDB8fHx8MTc2ODI1MzgyMnww&ixlib=rb-4.1.0&q=85',
      path: '/employes',
      color: 'bg-amber-500'
    },
    {
      id: 'siteweb',
      icon: Globe,
      title: 'Site Web',
      description: 'Création de sites web sur mesure avec toutes les fonctionnalités',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80',
      path: '/siteweb',
      color: 'bg-indigo-500'
    }
  ];

  const benefits = [
    { icon: Clock, title: "Temps retrouvé", description: "Automatisez les tâches répétitives" },
    { icon: Wallet, title: "Argent optimisé", description: "Réduisez vos coûts opérationnels" },
    { icon: Zap, title: "Liberté amplifiée", description: "Concentrez-vous sur l'essentiel" }
  ];

  return (
    <div className="min-h-screen" data-testid="landing-page">
      {/* Hero Section */}
      <section className="hero-gradient pt-32 pb-20 md:pt-40 md:pb-32 relative overflow-hidden">
        <div className="blue-glow absolute inset-0 pointer-events-none" />
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Text */}
            <div className="space-y-8 animate-fade-in">
              <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm border border-slate-200 rounded-full px-4 py-2 text-sm">
                <Sparkles className="w-4 h-4 text-amber-500" />
                <span className="text-slate-600">AGENTORY - Intelligence Artificielle</span>
              </div>
              
              <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-slate-900 leading-tight">
                {t('heroTitle')}
              </h1>
              
              <p className="text-lg text-slate-600 max-w-xl leading-relaxed">
                {t('heroSubtitle')}
              </p>
              
              <p className="text-primary font-medium italic border-l-4 border-primary pl-4">
                "{t('slogan')}"
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register">
                  <Button className="bg-primary hover:bg-primary-hover text-white rounded-full px-8 py-6 text-lg hover-lift shadow-lg" data-testid="hero-cta-btn">
                    {t('getStarted')}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/testimonials">
                  <Button variant="outline" className="rounded-full px-8 py-6 text-lg border-2" data-testid="hero-testimonials-btn">
                    {t('learnMore')}
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right: Smiling Robot Illustration */}
            <div className="relative hidden lg:flex justify-center items-center">
              <div className="w-80 h-80 bg-primary/10 rounded-full absolute animate-pulse" />
              <div className="w-64 h-64 bg-primary/20 rounded-full absolute animate-pulse delay-300" />
              <div className="relative z-10 animate-float">
                <div className="w-48 h-48 bg-white rounded-3xl shadow-2xl flex items-center justify-center">
                  <SmilingRobot className="w-24 h-24 text-primary" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div 
                key={benefit.title}
                className="flex items-start gap-4 p-6 rounded-2xl bg-slate-50 animate-fade-in"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <benefit.icon className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-lg text-slate-900">{benefit.title}</h3>
                  <p className="text-slate-600 text-sm mt-1">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Branches Section */}
      <section className="py-24 bg-slate-50" data-testid="branches-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16 space-y-4">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-slate-900">
              Nos Solutions
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Découvrez nos agents IA et services adaptés à chaque secteur d'activité
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
            {branches.map((branch, index) => (
              <Link to={branch.path} key={branch.id}>
                <Card 
                  className="card-hover overflow-hidden group cursor-pointer h-full animate-fade-in"
                  style={{ animationDelay: `${index * 100}ms` }}
                  data-testid={`branch-card-${branch.id}`}
                >
                  <div className="relative h-40 overflow-hidden">
                    <img
                      src={branch.image}
                      alt={branch.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className={`absolute top-4 left-4 w-10 h-10 ${branch.color} rounded-xl flex items-center justify-center`}>
                      <branch.icon className="w-5 h-5 text-white" />
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2 group-hover:text-primary transition-colors">
                      {branch.title}
                    </h3>
                    <p className="text-slate-600 text-sm">{branch.description}</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-primary relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-72 h-72 bg-white rounded-full translate-x-1/2 translate-y-1/2" />
        </div>
        
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative z-10">
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-6">
            Prêt à transformer votre activité ?
          </h2>
          <p className="text-blue-100 text-lg mb-8 max-w-2xl mx-auto">
            Rejoignez les entreprises qui ont déjà adopté nos solutions IA pour gagner en efficacité
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button className="bg-white text-primary hover:bg-slate-100 rounded-full px-8 py-6 text-lg hover-lift" data-testid="cta-register-btn">
                {t('getStarted')}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/appointment">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg" data-testid="cta-appointment-btn">
                {t('bookAppointment')}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
